import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Table,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Import the API path
import { getReviwQueueDeshbordPath } from "../../../../../../../services/config";
// Import your thunk or action
import { commonSaveData } from "../../../../../../../redux/slices/commonSlice";

const HomeLoan = () => {
  const dispatch = useDispatch();
  const [homeLoanData, setHomeLoanData] = useState(null);
  const [loading, setLoading] = useState(true); // Add local loading state

  // 1. Access Redux State
  const { 
    reviewQueueData, 
    reviewQueueLoading, 
    reviewQueueError 
  } = useSelector((state) => state.common || {});

  // 2. Get User Context from LocalStorage
  const storedUser = JSON.parse(localStorage.getItem("user_data") || "{}");
  const tdsUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");

  // 3. Fetch Data on Mount
  useEffect(() => {
    const fetchReviewQueueData = async () => {
      const staffId = tdsUser.staffId || tdsUser.id || tdsUser.employeeId || storedUser.staffId || storedUser.id;
      const userId = storedUser.user_id || storedUser.id || "123";
      
      if (staffId) {
        try {
          setLoading(true); // Set loading to true when starting fetch
          const payload = {
            path: getReviwQueueDeshbordPath,
            data: {
              userId: userId,
              staffId: Number(staffId),
              employeeId: "FY2025-26",
              section: "all",
              financialYear: storedUser.financialYear || "2025-26",
            },
          };
          
          console.log("Fetching Home Loan data with payload:", payload);
          const response = await dispatch(commonSaveData(payload)).unwrap();
          
          if (response && response.code === 200 && response.data) {
            setHomeLoanData(response.data);
          } else {
            throw new Error(response?.message || "Invalid API response");
          }
          
        } catch (error) {
          console.error("Error fetching review queue data:", error);
          toast.error("Failed to load home loan data");
        } finally {
          setLoading(false); // Set loading to false when done
        }
      } else {
        toast.warning("Please login again or select an employee");
        setLoading(false);
      }
    };

    fetchReviewQueueData();
  }, [dispatch, storedUser, tdsUser]);

  // 4. Loading State
  if (loading) { // Check local loading state instead
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: "#0f0f1a", color: "white" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading Home Loan Data...</span>
      </div>
    );
  }

  // 5. Error State
  if (reviewQueueError) {
    return (
      <div className="text-center text-danger p-5" style={{ backgroundColor: "#0f0f1a", minHeight: "100vh" }}>
        <h4>Error loading data</h4>
        <p>{reviewQueueError.message || "Unknown error occurred"}</p>
        <Button 
          variant="primary" 
          onClick={() => window.location.reload()}
          className="mt-3"
        >
          Retry
        </Button>
      </div>
    );
  }

  // 6. Check if we have data
  if (!homeLoanData) {
    return (
      <div className="text-center text-warning p-5" style={{ backgroundColor: "#0f0f1a", minHeight: "100vh" }}>
        <h4>No Data Available</h4>
        <p>Home loan data could not be loaded or is not available for this employee.</p>
        <Button 
          variant="primary" 
          onClick={() => window.location.reload()}
          className="mt-3"
        >
          Try Again
        </Button>
      </div>
    );
  }

  // 7. Data Extraction from API Response
  const employeeInfo = homeLoanData.employeeInfo || {};
  const sections = homeLoanData.sections || {};
  const overallStatus = homeLoanData.overallStatus || {};
  
  // Extract home loan data - check multiple possible structures
  const homeLoanSection = sections.homeLoan || sections.housingLoan || {};
  const documentsSection = sections.documents || homeLoanData.documentsSection || {};
  
  // Extract fields from homeLoanSection
  const fields = homeLoanSection.fields || {};
  
  // Helper function to safely extract values
  const getFieldValue = (fieldName, defaultValue = "") => {
    const field = fields[fieldName];
    if (field && field.value !== undefined && field.value !== null && field.value !== "") {
      return field.value;
    }
    // If not in fields, check directly in homeLoanSection
    if (homeLoanSection[fieldName] !== undefined && homeLoanSection[fieldName] !== null && homeLoanSection[fieldName] !== "") {
      return homeLoanSection[fieldName];
    }
    return defaultValue;
  };

  // Extract loan details from API
  const lenderName = getFieldValue("lenderName");
  const lenderPan = getFieldValue("lenderPan");
  const propertyAddress = getFieldValue("propertyAddress");
  const ownershipType = getFieldValue("ownershipType");
  const loanAccountNo = getFieldValue("loanAccountNo");
  const loanAmount = parseInt(getFieldValue("loanAmount", "0")) || 0;
  const interestRate = parseFloat(getFieldValue("interestRate", "0")) || 0;
  const tenure = parseInt(getFieldValue("tenure", "0")) || 0;
  const startDate = getFieldValue("startDate");
  const loanPurpose = getFieldValue("loanPurpose");
  const finalMaturityDate = getFieldValue("finalMaturityDate");
  
  // Extract interest calculations
  const interestPaid = parseInt(getFieldValue("interestPaid", "0")) || 0;
  const maxDeduction = ownershipType === "Self-occupied" ? 200000 : 0;
  const eligibleDeduction = Math.min(interestPaid, maxDeduction);
  
  // Extract document information - check API structure for documents
  const getDocumentStatus = (docType) => {
    if (!documentsSection || typeof documentsSection !== 'object') return false;
    
    // Check multiple possible structures
    const doc = documentsSection[docType] || 
                documentsSection[docType.toLowerCase()] ||
                documentsSection[docType + 'Document'] ||
                (documentsSection.documents && documentsSection.documents[docType]);
    
    return doc ? (doc.url || doc.status === "uploaded" || doc === true) : false;
  };

  const hasLenderCertificate = getDocumentStatus("lenderCertificate") || 
                              getDocumentStatus("homeLoanCertificate") ||
                              getDocumentStatus("form12BB");
  
  const hasLoanStatement = getDocumentStatus("loanStatement") || 
                          getDocumentStatus("accountStatement");
  
  const hasAgreementCopy = getDocumentStatus("agreementCopy") || 
                          getDocumentStatus("loanAgreement");

  // Get document URL if available
  const getDocumentUrl = (docType) => {
    if (!documentsSection || typeof documentsSection !== 'object') return null;
    
    const doc = documentsSection[docType] || 
                documentsSection[docType.toLowerCase()] ||
                documentsSection[docType + 'Document'] ||
                (documentsSection.documents && documentsSection.documents[docType]);
    
    return doc ? (doc.url || doc.path || doc.fileUrl) : null;
  };

  const lenderCertificateUrl = getDocumentUrl("lenderCertificate") || 
                              getDocumentUrl("homeLoanCertificate") ||
                              getDocumentUrl("form12BB");

  // Extract OCR analysis data - check API structure
  const ocrAnalysis = homeLoanData.ocrAnalysis || documentsSection.ocrAnalysis || {};
  const ocrStatus = ocrAnalysis.status || "Not Available";
  const ocrConfidence = ocrAnalysis.confidence || ocrAnalysis.confidenceScore || 0;
  
  // Extract remarks and audit trail
  const remarks = getFieldValue("remarks") || 
                  homeLoanData.remarks || 
                  overallStatus.remarks ||
                  "No remarks available";
  
  const lastActionBy = homeLoanData.lastActionBy || overallStatus.lastActionBy || "";
  const lastActionDate = homeLoanData.lastActionDate || overallStatus.lastActionDate || "";

  // Extract validation data
  const isPanValid = lenderPan && lenderPan.length === 10;
  const isWithinLimit = interestPaid <= maxDeduction;
  const documentStatus = hasLenderCertificate ? "Verified" : "Pending";

  // Calculate tenure completed (if we have start date)
  const calculateTenureCompleted = () => {
    if (!startDate) return 0;
    try {
      const start = new Date(startDate);
      const now = new Date();
      const diffYears = now.getFullYear() - start.getFullYear();
      return Math.min(diffYears, tenure);
    } catch {
      return 0;
    }
  };

  const tenureCompleted = calculateTenureCompleted();

  // 8. Action Handlers
  const handleApproveDeduction = async () => {
    try {
      setLoading(true);
      const staffId = tdsUser.staffId || storedUser.staffId;
      const userId = storedUser.user_id || storedUser.id;
      
      const payload = {
        path: "/tds/action/approve",
        data: {
          userId: userId,
          staffId: Number(staffId),
          section: "homeLoan",
          action: "approve",
          financialYear: storedUser.financialYear || "2025-26",
          deductionAmount: eligibleDeduction
        },
      };
      
      const response = await dispatch(commonSaveData(payload)).unwrap();
      
      if (response.code === 200) {
        toast.success("Home loan deduction approved successfully!");
        window.location.reload();
      } else {
        toast.error(response.message || "Approval failed");
      }
      
    } catch (error) {
      console.error("Error approving deduction:", error);
      toast.error("Failed to approve deduction");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      const staffId = tdsUser.staffId || storedUser.staffId;
      const userId = storedUser.user_id || storedUser.id;
      
      const payload = {
        path: "/tds/action/reject",
        data: {
          userId: userId,
          staffId: Number(staffId),
          section: "homeLoan",
          action: "reject",
          financialYear: storedUser.financialYear || "2025-26",
          reason: "Document verification pending"
        },
      };
      
      const response = await dispatch(commonSaveData(payload)).unwrap();
      
      if (response.code === 200) {
        toast.success("Home loan deduction rejected!");
        window.location.reload();
      } else {
        toast.error(response.message || "Rejection failed");
      }
      
    } catch (error) {
      console.error("Error rejecting deduction:", error);
      toast.error("Failed to reject deduction");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const staffId = tdsUser.staffId || storedUser.staffId;
      const userId = storedUser.user_id || storedUser.id;
      
      const payload = {
        path: getReviwQueueDeshbordPath,
        data: {
          userId: userId,
          staffId: Number(staffId),
          employeeId: "FY2025-26",
          section: "all",
          financialYear: storedUser.financialYear || "2025-26",
        },
      };
      
      const response = await dispatch(commonSaveData(payload)).unwrap();
      
      if (response && response.code === 200 && response.data) {
        setHomeLoanData(response.data);
        toast.success("Data refreshed successfully!");
      } else {
        toast.error("Failed to refresh data");
      }
      
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="p-4" 
      style={{ 
        backgroundColor: "#0f0f1a", 
        minHeight: '100vh',
        color: "#e0e0e0",
        fontFamily: 'Arial, sans-serif'
      }}
    >
      {/* Main Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-white mb-2" style={{ fontWeight: 600, color: "#6f42c1" }}>
            Home Loan Interest Deduction
          </h2>
          <p className="text-muted mb-3">
            Source and use of home loan interest deduction under Section 24(b)
          </p>
          
          <div className="d-flex align-items-center gap-3">
            <Badge bg="primary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              Employee: {employeeInfo.employeeName || employeeInfo.fullName || "N/A"}
            </Badge>
            <Badge bg="info" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              PAN: {employeeInfo.employeePAN || employeeInfo.panNumber || "N/A"}
            </Badge>
            <Badge bg={ownershipType ? "success" : "secondary"} className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              {ownershipType || "Type not specified"}
            </Badge>
            <Badge bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              FY {homeLoanData.financialYear || storedUser.financialYear || "2025-26"}
            </Badge>
          </div>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={handleRefresh}
            className="me-2"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Refresh"}
          </Button>
          <Button 
            variant="outline-info" 
            size="sm"
            onClick={() => toast.info("Export feature coming soon")}
            disabled={loading}
          >
            Export
          </Button>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Left Column - Loan Details & Calculations */}
        <Col lg={8}>
          {/* Loan Details Card */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Loan Details</h5>
              <Badge bg={isPanValid ? "success" : lenderPan ? "danger" : "secondary"}>
                {isPanValid ? "PAN Valid" : lenderPan ? "PAN Invalid" : "PAN Missing"}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Lender Name</label>
                    <div className={`p-2 rounded ${lenderName ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {lenderName || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Lender PAN</label>
                    <div className={`p-2 rounded ${lenderPan ? "text-white fw-bold bg-dark" : "text-muted bg-secondary"}`}>
                      {lenderPan || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Loan Account Number</label>
                    <div className={`p-2 rounded ${loanAccountNo ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {loanAccountNo || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Loan Amount</label>
                    <div className={`p-2 rounded ${loanAmount > 0 ? "text-white fw-bold bg-dark" : "text-muted bg-secondary"}`}>
                      {loanAmount > 0 ? `₹${loanAmount.toLocaleString('en-IN')}` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Interest Rate</label>
                    <div className={`p-2 rounded ${interestRate > 0 ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {interestRate > 0 ? `${interestRate}% p.a.` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Tenure</label>
                    <div className={`p-2 rounded ${tenure > 0 ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {tenure > 0 ? `${tenure} years` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Start Date</label>
                    <div className={`p-2 rounded ${startDate ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {startDate || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Property Address</label>
                    <div className={`p-2 rounded ${propertyAddress ? "text-white bg-dark" : "text-muted bg-secondary"}`} style={{ minHeight: '60px' }}>
                      {propertyAddress || "Not provided"}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Interest Deduction Calculation */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Interest Deduction Computation</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="d-flex justify-content-between p-3 border-bottom border-secondary">
                    <span>Interest Paid (Declared)</span>
                    <strong className="text-white">₹{interestPaid.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-between p-3 border-bottom border-secondary">
                    <span>Max Limit ({ownershipType || "N/A"})</span>
                    <strong className="text-white">₹{maxDeduction.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>
              </Row>

              {/* Calculation Result */}
              <div className="text-center mb-4">
                <div className="p-4 rounded" style={{ 
                  background: "linear-gradient(135deg, rgba(106, 17, 203, 0.2), rgba(37, 117, 252, 0.2))",
                  border: "2px solid #6f42c1"
                }}>
                  <h6 className="text-muted mb-3">Eligible Deduction u/s 24(b)</h6>
                  <h1 className="display-5 fw-bold mb-3 text-white">₹{eligibleDeduction.toLocaleString('en-IN')}</h1>
                  <p className="text-muted mb-0">
                    Lower of Interest Paid (₹{interestPaid.toLocaleString('en-IN')}) 
                    or Max Limit (₹{maxDeduction.toLocaleString('en-IN')})
                  </p>
                </div>
              </div>

              {/* Utilization Criteria */}
              <div className="mt-4">
                <h6 className="text-white mb-3" style={{ fontWeight: 600 }}>Utilization Criteria</h6>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Loan Purpose</div>
                      <div className="text-white">{loanPurpose || "Not specified"}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Tenure Completed</div>
                      <div className="text-white">{tenureCompleted} years</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Final Maturity Date</div>
                      <div className="text-white">{finalMaturityDate || "Not specified"}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>

          {/* Discussions & Remarks Section */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Discussions & Remarks</h5>
            </Card.Header>
            <Card.Body>
              <div className="p-3 bg-dark rounded mb-3">
                <div className="text-muted small mb-1">Remarks/Notes</div>
                <div className="text-white">
                  {remarks}
                </div>
              </div>
              
              <Row className="g-3">
                <Col md={6}>
                  <div className="p-3 bg-dark rounded">
                    <div className="text-muted small mb-1">Last Action By</div>
                    <div className="text-white">{lastActionBy || "N/A"}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 bg-dark rounded">
                    <div className="text-muted small mb-1">Last Action Date</div>
                    <div className="text-white">{lastActionDate || "N/A"}</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Documents & Actions */}
        <Col lg={4}>
          {/* Document Guidelines */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Document Guidelines</h5>
              <Badge bg={hasLenderCertificate ? "success" : "warning"}>
                {hasLenderCertificate ? "Complete" : "Pending"}
              </Badge>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <ProgressBar 
                  now={hasLenderCertificate ? 100 : hasLoanStatement ? 50 : 0} 
                  label={hasLenderCertificate ? "Complete" : "In Progress"}
                  variant={hasLenderCertificate ? "success" : "warning"}
                  style={{ height: "12px", borderRadius: "6px" }}
                />
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded mb-2">
                  <div>
                    <span className="text-white">Lender Certificate</span>
                    <div className="text-muted small">Form 12BB/Home Loan Certificate</div>
                  </div>
                  <Badge bg={hasLenderCertificate ? "success" : "danger"}>
                    {hasLenderCertificate ? "Uploaded" : "Missing"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded mb-2">
                  <div>
                    <span className="text-white">Loan Statement PDF</span>
                    <div className="text-muted small">Loan Account Statement</div>
                  </div>
                  <Badge bg={hasLoanStatement ? "success" : "warning"}>
                    {hasLoanStatement ? "Uploaded" : "Optional"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded">
                  <div>
                    <span className="text-white">Agreement Copy</span>
                    <div className="text-muted small">Loan Agreement Copy</div>
                  </div>
                  <Badge bg={hasAgreementCopy ? "success" : "secondary"}>
                    {hasAgreementCopy ? "Uploaded" : "Not Required"}
                  </Badge>
                </div>
              </div>
              
              {hasLenderCertificate && lenderCertificateUrl && (
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  className="w-100"
                  onClick={() => window.open(lenderCertificateUrl, '_blank')}
                >
                  View Lender Certificate
                </Button>
              )}
            </Card.Body>
          </Card>

          {/* OCR Analysis Section */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>OCR Analysis</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Document Status</span>
                  <Badge bg={ocrStatus === "Verified" ? "success" : "warning"}>
                    {ocrStatus}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Confidence Score</span>
                  <Badge bg={ocrConfidence > 90 ? "success" : "warning"}>
                    {ocrConfidence > 0 ? `${ocrConfidence}%` : "N/A"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Extracted Data</span>
                  <Badge bg={lenderName || lenderPan || loanAmount > 0 ? "info" : "secondary"}>
                    {lenderName || lenderPan || loanAmount > 0 ? "Available" : "N/A"}
                  </Badge>
                </div>
                
                <div className="mt-3 p-3 bg-dark rounded">
                  <div className="text-muted small mb-1">Extracted Information</div>
                  <div className="text-white small">
                    {lenderName ? `• Lender: ${lenderName}` : ""}
                    {lenderPan ? `\n• PAN: ${lenderPan}` : ""}
                    {loanAmount > 0 ? `\n• Amount: ₹${loanAmount.toLocaleString('en-IN')}` : ""}
                    {interestRate > 0 ? `\n• Interest Rate: ${interestRate}%` : ""}
                    {!lenderName && !lenderPan && !loanAmount && !interestRate ? "No data extracted" : ""}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Validation Status */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Validation Status</h5>
            </Card.Header>
            <Card.Body>
              <Table borderless className="text-white mb-0">
                <tbody>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Lender PAN</span>
                        {lenderPan ? (isPanValid ? "✓" : "✗") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {lenderPan ? (
                        isPanValid ? (
                          <Badge bg="success">Valid</Badge>
                        ) : (
                          <Badge bg="danger">Invalid</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Missing</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Limit Check</span>
                        {interestPaid > 0 ? (isWithinLimit ? "✓" : "⚠") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {interestPaid > 0 ? (
                        isWithinLimit ? (
                          <Badge bg="success">Within Limit</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Exceeds</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Not Declared</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Document Status</span>
                        {hasLenderCertificate ? "✓" : "✗"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {hasLenderCertificate ? (
                        <Badge bg="success">Verified</Badge>
                      ) : (
                        <Badge bg="danger">Pending</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Overall Status</span>
                        {lenderPan && interestPaid > 0 && hasLenderCertificate ? (isPanValid && isWithinLimit ? "✓" : "⚠") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end">
                      {lenderPan && interestPaid > 0 && hasLenderCertificate ? (
                        isPanValid && isWithinLimit ? (
                          <Badge bg="success">Approved</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Review Needed</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Incomplete</Badge>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-3">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={handleApproveDeduction}
                  disabled={!isPanValid || !isWithinLimit || !hasLenderCertificate || eligibleDeduction <= 0 || loading}
                  className="py-3"
                >
                  <div className="d-flex flex-column">
                    <span>Approve Deduction</span>
                    <small className="opacity-75">₹{eligibleDeduction.toLocaleString('en-IN')}</small>
                  </div>
                </Button>
                
                <Button 
                  variant="danger" 
                  size="lg"
                  onClick={handleReject}
                  className="py-3"
                  disabled={loading}
                >
                  Reject Application
                </Button>
                
                <Button 
                  variant="outline-info" 
                  size="lg"
                  onClick={() => toast.info("Request sent to employee")}
                  disabled={hasLenderCertificate || loading}
                  className="py-3"
                >
                  Request Additional Documents
                </Button>
                
                <Button 
                  variant="outline-light" 
                  size="lg"
                  onClick={() => toast.info("Detailed view coming soon")}
                  className="py-3"
                  disabled={loading}
                >
                  View Full Audit Trail
                </Button>
              </div>
              
              <div className="mt-4 p-3 bg-dark rounded">
                <small className="text-muted">
                  <strong>Note:</strong> Home loan interest deduction under Section 24(b) is limited to ₹2,00,000 per financial year for self-occupied property. Document verification is mandatory for approval.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomeLoan;